import { httpSessionHandoffApi } from '@/infrastructure/auth/HttpSessionHandoffApi'
import { COGNITO_KEY_PREFIX, acceptedClientIds, activeCognitoPool, dropClientKeys, localStorageStore } from '@/utils/cognitoPool'

/**
 * Handoff de sesion de Cognito entre subdominios.
 *
 * El regimen permanente sigue siendo localStorage. Al saltar de ambiente la sesion
 * se publica en cookies de dominio padre de vida corta, y en el origen destino el
 * adapter de esta misma unidad las consume en el primer miss, las importa y las
 * borra en el acto.
 *
 * Outside the US region the cookie path is off: EU tenants are flat `<sub>.finnecto.com` hosts,
 * so a `.finnecto.com` cookie would reach US hosts. There the session travels as a one-time code
 * issued and redeemed by this build's own API (`VITE_API_URL`), never redeemable in another region.
 *
 * ponytail: NO usar CookieStorage de amazon-cognito-identity-js. Su clear() itera
 * todas las cookies del documento y las borra, asi que con un dominio padre barreria
 * cookies ajenas. Ver seccion 2.4 de la spec.
 */

export { COGNITO_KEY_PREFIX, localStorageStore }

/**
 * Prefijo de las cookies de handoff. Una cookie por llave, no una sola con todo:
 * el limite de ~4KB por cookie no alcanza para id + access + refresh juntos.
 */
export const HANDOFF_COOKIE_PREFIX = 'fto_ho.'

/**
 * Cookie que cruza el metodo de auth entre subdominios. `authMethod` vive en el store
 * de pinia (persist -> localStorage) que es por origin, asi que al saltar de ambiente el
 * destino arranca sin el y defaultea a Cognito: para un usuario SSO eso manda el token
 * equivocado y la pantalla queda en blanco. Es de un solo uso: se consume y se borra.
 */
export const AUTH_METHOD_COOKIE = 'fto_ho_auth'

/**
 * La interfaz minima de localStorage que consume el handoff. Recibida por parametro
 * para que la logica sea testeable: vitest corre en environment 'node'.
 */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  clear(): void
  keys(): string[]
}

export type HandoffMechanism = 'cookie' | 'code'

const US_REGION = 'us-east-2'

/** The cookie handoff stays only in the US region (today's behaviour); any other region uses the code. */
export function handoffMechanism(awsRegion: string | undefined): HandoffMechanism {
  return awsRegion?.trim() === US_REGION ? 'cookie' : 'code'
}

function currentMechanism(): HandoffMechanism {
  return handoffMechanism(import.meta.env.VITE_AWS_REGION)
}

/** Cookie domain for this host, or null when the cookie handoff is off (non-US region, localhost). */
function cookieHandoffDomain(): string | null {
  return currentMechanism() === 'cookie' ? resolveCookieDomain(window.location.hostname) : null
}

/**
 * Dominio para las cookies de handoff, o null si el host no comparte dominio padre
 * con otros ambientes y por lo tanto el handoff no aplica.
 *
 * El orden importa: sandbox se evalua primero porque un host de sandbox tambien
 * termina en .finnecto.com y quedaria con el dominio equivocado.
 */
export function resolveCookieDomain(hostname: string): string | null {
  if (hostname.endsWith('.sandbox.finnecto.com')) return '.sandbox.finnecto.com'
  if (hostname.endsWith('.finnecto.com')) return '.finnecto.com'
  return null
}

/**
 * El ambiente de origen al que se puede volver, o null si no hay uno confiable.
 *
 * Vive aca y no en la vista para que el conocimiento del sufijo .finnecto.com tenga un
 * solo dueno, junto a resolveCookieDomain.
 *
 * El referrer llega gracias al Referrer-Policy strict-origin-when-cross-origin de
 * amplify.yml:21-26: en una navegacion cross-origin el browser manda solo el origen.
 * Descarta lo que no sea un ambiente de Finnecto, y el propio origen: volver a donde
 * ya estamos no ayuda a nadie.
 */
export function resolveOriginEnvironment(referrer: string, currentOrigin: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(referrer)
  } catch {
    return null
  }
  if (!parsed.hostname.endsWith('.finnecto.com')) return null
  if (parsed.origin === currentOrigin) return null
  return parsed.origin
}

/** Llaves de Cognito presentes en el store, para publicar. */
export function collectSessionEntries(store: KeyValueStore): Record<string, string> {
  const entries: Record<string, string> = {}
  for (const key of store.keys()) {
    if (!key.startsWith(COGNITO_KEY_PREFIX)) continue
    const value = store.getItem(key)
    if (value !== null) entries[key] = value
  }
  return entries
}

/** Llaves de Cognito presentes en un header de cookies, para consumir. */
export function parseHandoffCookies(cookieHeader: string): Record<string, string> {
  const entries: Record<string, string> = {}
  for (const chunk of cookieHeader.split('; ')) {
    const separator = chunk.indexOf('=')
    if (separator < 0) continue
    const name = chunk.slice(0, separator)
    if (!name.startsWith(HANDOFF_COOKIE_PREFIX)) continue
    entries[name.slice(HANDOFF_COOKIE_PREFIX.length)] = decodeURIComponent(chunk.slice(separator + 1))
  }
  return entries
}

/** El metodo de auth presente en un header de cookies, o null. Puro, para testear. */
export function parseHandoffAuthMethod(cookieHeader: string): string | null {
  for (const chunk of cookieHeader.split('; ')) {
    const separator = chunk.indexOf('=')
    if (separator < 0) continue
    if (chunk.slice(0, separator) !== AUTH_METHOD_COOKIE) continue
    const value = decodeURIComponent(chunk.slice(separator + 1))
    return value === '' ? null : value
  }
  return null
}

/** El transporte de cookies, inyectado para que el adapter sea testeable sin DOM. */
export interface HandoffPort {
  read(): Record<string, string>
  drop(keys: string[]): void
}

/**
 * Implementa la forma de ICognitoStorage de amazon-cognito-identity-js
 * (getItem/setItem/removeItem/clear, sincrona) delegando en localStorage, con un
 * unico comportamiento propio: en el primer miss consume la cookie de handoff.
 *
 * Se pasa a Auth.configure({ storage }) en configureAmplifyAtBoot. Amplify decide cuando lee, asi
 * que no hay que acertar ningun momento de invocacion desde el guard.
 */
export class HandoffCognitoStorage {
  private consumed = false

  constructor(
    private readonly store: KeyValueStore,
    private readonly handoff: HandoffPort,
  ) {}

  getItem(key: string): string | null {
    const direct = this.store.getItem(key)
    // Camino de acierto: el 100% de los usuarios que no cambian de ambiente pasa por
    // aca y no ejercita nada mas. Tiene que ser indistinguible del localStorage pelado.
    if (direct !== null) return direct

    if (this.consumed) return null
    this.consumed = true

    const incoming = this.handoff.read()
    const keys = Object.keys(incoming)
    if (keys.length === 0) return null

    // ponytail: borrar ANTES de responder, no al final del bootstrap. Diferirlo deja la
    // ventana de exposicion abierta durante todo el arranque, y para siempre si algo
    // revienta en el medio.
    this.handoff.drop(keys)
    for (const importedKey of keys) this.store.setItem(importedKey, incoming[importedKey])

    return this.store.getItem(key)
  }

  setItem(key: string, value: string): void { this.store.setItem(key, value) }

  removeItem(key: string): void { this.store.removeItem(key) }

  /** Delega tal cual: el signOut tiene que comportarse exactamente como hoy. */
  clear(): void { this.store.clear() }
}

/**
 * Techo de vida de la cookie, en SEGUNDOS. Tiene que cubrir "clickeo el ambiente,
 * navega, arranca la app, Amplify pide la sesion" y nada mas. En minutos u horas
 * deja de ser una ventana y se convierte en el storage compartido permanente que
 * este diseno evita: la cookie viaja a api.finnecto.com (spec 2.5).
 */
const HANDOFF_MAX_AGE_SECONDS = 30

function cookieAttributes(domain: string, maxAge: number): string {
  return `Domain=${domain}; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`
}

/**
 * Puerto real sobre document.cookie. Devuelve un no-op si el host no comparte
 * dominio padre (localhost, amplifyapp), donde el handoff no aplica.
 */
function documentHandoffPort(): HandoffPort {
  const domain = cookieHandoffDomain()
  if (domain === null) return { read: () => ({}), drop: () => {} }

  return {
    read: () => parseHandoffCookies(document.cookie),
    drop: (keys) => {
      for (const key of keys) {
        document.cookie = `${HANDOFF_COOKIE_PREFIX}${key}=; ${cookieAttributes(domain, 0)}`
      }
    },
  }
}

/** El storage que se le pasa a Auth.configure en configureAmplifyAtBoot. */
export function createCognitoStorage(): HandoffCognitoStorage {
  return new HandoffCognitoStorage(localStorageStore(), documentHandoffPort())
}

/**
 * Publica la sesion actual en cookies de dominio padre, una por llave. Sincrona a
 * proposito: lee localStorage directo, sin Auth.currentSession(), asi puede correr en
 * el @click de un <a> antes de que el browser navegue, sin romper el href.
 *
 * Publica los tokens tal como estan, incluso vencidos: el refreshToken viaja con
 * ellos y Amplify renueva en el destino.
 */
export function publishSessionHandoff(authMethod?: string): void {
  const domain = cookieHandoffDomain()
  if (domain === null) return

  const entries = collectSessionEntries(localStorageStore())
  for (const [key, value] of Object.entries(entries)) {
    document.cookie =
      `${HANDOFF_COOKIE_PREFIX}${key}=${encodeURIComponent(value)}; ` +
      cookieAttributes(domain, HANDOFF_MAX_AGE_SECONDS)
  }

  if (authMethod) {
    document.cookie =
      `${AUTH_METHOD_COOKIE}=${encodeURIComponent(authMethod)}; ` +
      cookieAttributes(domain, HANDOFF_MAX_AGE_SECONDS)
  }
}

/**
 * Lee el metodo de auth cruzado desde el ambiente de origen y borra la cookie en el acto.
 * Devuelve null si no comparte dominio padre (localhost) o si no hay cookie.
 */
export function consumeHandoffAuthMethod(): string | null {
  const domain = cookieHandoffDomain()
  if (domain === null) return null

  const authMethod = parseHandoffAuthMethod(document.cookie)
  if (authMethod !== null) {
    document.cookie = `${AUTH_METHOD_COOKIE}=; ${cookieAttributes(domain, 0)}`
  }
  return authMethod
}

/** Fragment key carrying the one-time code (a fragment never reaches a server, a log or a Referer). Also in index.html. */
export const HANDOFF_FRAGMENT_KEY = 'handoff'

/** Fragment key for the SSO marker. Not a secret: it only starts the Auth0 redirect. Also in index.html. */
export const HANDOFF_AUTH_FRAGMENT_KEY = 'handoff_auth'

/** Caps the wait before navigating; on timeout the target just asks for login. */
const ISSUE_TIMEOUT_MS = 5000

export type AuthMethod = 'auth0' | 'cognito'

/**
 * finnecto-api PLA-396: issue takes `{ entries }` (non-empty, holding the bearer token itself) and returns
 * `{ code }`; redeem returns `{ entries }`. `bearer()` may refresh the session in storage.
 */
export interface HandoffApi {
  bearer(): Promise<string>
  issue(entries: Record<string, string>, bearer: string): Promise<string | null>
  redeem(code: string): Promise<Record<string, unknown> | null>
}

/** Only the given Cognito clients' keys (`CognitoIdentityServiceProvider.<clientId>.*`): each region has its own client. */
export function ownClientEntries(entries: Record<string, unknown>, clientIds: string[]): Record<string, string> {
  const prefixes = clientIds.map((clientId) => `${COGNITO_KEY_PREFIX}${clientId}.`)
  const own: Record<string, string> = {}
  for (const [key, value] of Object.entries(entries)) {
    if (prefixes.some((prefix) => key.startsWith(prefix)) && typeof value === 'string') own[key] = value
  }
  return own
}

/** Token keys Amplify keeps per signed-in user, under `CognitoIdentityServiceProvider.<clientId>.<user>.`. */
const SESSION_ENTRY_SUFFIXES = ['idToken', 'accessToken', 'refreshToken', 'clockDrift', 'userData']

/**
 * The current session of this build's client: `LastAuthUser` plus that user's token keys that exist.
 * A previous user's keys stay in storage until signOut, and the API refuses any key outside the bearer's
 * session; no `LastAuthUser` means no session.
 */
export function currentSessionEntries(store: KeyValueStore, clientId: string): Record<string, string> {
  const prefix = `${COGNITO_KEY_PREFIX}${clientId}.`
  const lastAuthUserKey = `${prefix}LastAuthUser`
  const user = store.getItem(lastAuthUserKey)
  if (user === null) return {}
  const entries: Record<string, string> = { [lastAuthUserKey]: user }
  for (const suffix of SESSION_ENTRY_SUFFIXES) {
    const key = `${prefix}${user}.${suffix}`
    const value = store.getItem(key)
    if (value !== null) entries[key] = value
  }
  return entries
}

/** `targetUrl` with `params` appended to its fragment, keeping any fragment it already had. */
export function withHandoffFragment(targetUrl: string, params: Record<string, string>): string {
  const url = new URL(targetUrl)
  const added = Object.entries(params).map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
  if (added.length === 0) return url.toString()
  const existing = url.hash.replace(/^#/, '')
  url.hash = [...(existing ? [existing] : []), ...added].join('&')
  return url.toString()
}

/**
 * Fragment params for the target. Auth0 users only get the marker: their session lives in Auth0, and
 * skipping the issue call keeps getBearerToken (and its logout on failure) off the source tab.
 */
export async function issueSessionHandoff(
  authMethod: AuthMethod | undefined,
  api: HandoffApi,
  store: KeyValueStore,
  clientId: string,
): Promise<Record<string, string>> {
  if (authMethod === 'auth0') return { [HANDOFF_AUTH_FRAGMENT_KEY]: 'auth0' }

  if (Object.keys(currentSessionEntries(store, clientId)).length === 0) return {}
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), ISSUE_TIMEOUT_MS))
  try {
    const code = await Promise.race([issueWithFreshEntries(api, store, clientId), timeout])
    return code === null ? {} : { [HANDOFF_FRAGMENT_KEY]: code }
  } catch {
    return {}
  }
}

/** Bearer first: it may refresh the stored tokens, and the API requires the sent token among the entries. */
async function issueWithFreshEntries(api: HandoffApi, store: KeyValueStore, clientId: string): Promise<string | null> {
  const bearer = await api.bearer()
  return api.issue(currentSessionEntries(store, clientId), bearer)
}

/**
 * Redeems a code against this region's API and imports the accepted clients' keys; the imported session
 * replaces any other accepted client's session on this origin. True when something was imported.
 */
export async function redeemSessionHandoff(
  code: string,
  api: HandoffApi,
  store: KeyValueStore,
  clientIds: string[],
): Promise<boolean> {
  let entries: Record<string, unknown> | null
  try {
    entries = await api.redeem(code)
  } catch {
    return false
  }
  if (entries === null) return false

  const own = ownClientEntries(entries, clientIds)
  if (Object.keys(own).length === 0) return false
  for (const clientId of clientIds) {
    if (Object.keys(ownClientEntries(own, [clientId])).length === 0) dropClientKeys(store, clientId)
  }
  // LastAuthUser last: other tabs follow it and must find the tokens already there.
  const ordered = Object.entries(own).sort(([a], [b]) => Number(a.endsWith('.LastAuthUser')) - Number(b.endsWith('.LastAuthUser')))
  for (const [key, value] of ordered) store.setItem(key, value)
  return true
}

declare global {
  interface Window {
    /** Set by the `session-handoff-strip` script in index.html, read once here. */
    __finnectoHandoffCode?: string
    __finnectoHandoffAuth?: string
  }
}

let pendingAuthMethod: Promise<AuthMethod | null> | null = null

/** Starts redeeming what index.html took off the URL. Call once, before the router installs. */
export function startSessionHandoff(): void {
  const code = window.__finnectoHandoffCode
  const auth = window.__finnectoHandoffAuth
  delete window.__finnectoHandoffCode
  delete window.__finnectoHandoffAuth

  if (auth === 'auth0') {
    pendingAuthMethod = Promise.resolve('auth0')
    return
  }
  if (!code) return
  pendingAuthMethod = redeemSessionHandoff(code, httpSessionHandoffApi, localStorageStore(), acceptedClientIds())
    .then((imported): AuthMethod | null => (imported ? 'cognito' : null))
    .catch(() => null)
}

/** The auth method the handoff carried, once per page load; null when there was none or it failed. */
export async function consumeSessionHandoff(): Promise<AuthMethod | null> {
  const pending = pendingAuthMethod
  pendingAuthMethod = null
  return pending ?? null
}

/**
 * @click of a link to another tenant host. US: publishes the cookies and lets the browser navigate.
 * Elsewhere: issues a code, then navigates with it; a modified mouse click opens the tab before the call.
 */
export function handOffSession(event: MouseEvent | KeyboardEvent, targetUrl: string, authMethod?: AuthMethod): void {
  if (currentMechanism() === 'cookie') {
    publishSessionHandoff(authMethod)
    return
  }
  const mouse = event instanceof MouseEvent ? event : null
  if (event.defaultPrevented || (mouse !== null && mouse.button > 0)) return
  event.preventDefault()

  const newTab = mouse !== null && (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey)
  const tab = newTab ? window.open('', '_blank') : null
  const target = new URL(targetUrl, window.location.href).toString()
  void issueSessionHandoff(authMethod, httpSessionHandoffApi, localStorageStore(), activeCognitoPool().clientId)
    .then((params) => {
      const destination = withHandoffFragment(target, params)
      if (tab === null) {
        window.location.assign(destination)
        return
      }
      tab.opener = null
      tab.location.href = destination
    })
}
